import React, { useState, useEffect } from 'react';
import { AIQueryTypes, AskRequest } from '@hone-automation/common';
import { useTransactionDetails, EnhancedAskRequest } from 'hooks/useTransactionDetails';
import { useAIQuery } from 'presentation/components/KAI/KAI.hook';
import { parseAIResponse } from 'presentation/utils';
import summarizePrompt from 'presentation/components/KAI/SystemPrompts/Summarize';
import KAILoader from 'presentation/components/KAI/KAILoader';
import DOMPurify from 'dompurify';
import './SummarizePanel.scss';
import { logger } from 'presentation/components/KSExport/reportNTParser/utils';
import { IconCopyNew, IconThumbsDown, IconThumbsUp, IconCheck } from 'components/Icons';
import { Tooltip, Whisper } from 'rsuite';
import { useAuthContext } from 'context/useAuthContext';
import { HoneAbilityActions, HoneAbilitySubjects } from '@hone-automation/common';
import { abilityCan } from 'lib/utils';
import { useAIExtendedContext } from 'hooks/useAIExtendedContext';
interface SummarizePanelProps {
  request: AskRequest;
  trxURL?: string;
  className?: string;
}

interface SummaryResponse {
  isTestThread: boolean;
  summary: string;
  formattedSummary: string;
  actions: string[];
  involvedAccounts: string[];
  priority: {
    level: 'critical' | 'high' | 'medium' | 'low';
    reason: string;
    daysOutstanding: number;
  };
  confidenceScore: number;
}

/**
 * Panel content for request summarization - can be used in both desktop and mobile views
 */
const SummarizePanel: React.FC<SummarizePanelProps> = ({ request, trxURL, className }) => {
  // Use our transaction details hook to get formatted data
  const details = useTransactionDetails(request as EnhancedAskRequest);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [isCopying, setIsCopying] = useState(false);

  const { currentLocationAbilities } = useAuthContext();
  const extendedContext = useAIExtendedContext();
  const isAccountant = abilityCan(
    currentLocationAbilities,
    HoneAbilityActions.read,
    HoneAbilitySubjects.Bookkeeper_Controls
  );

  const handleCopySummary = async () => {
    if (!summary?.summary || isCopying) return;

    try {
      setIsCopying(true);
      await navigator.clipboard.writeText(summary.summary);

      // Revert back to copy icon after 2 seconds
      const timer = setTimeout(() => {
        setIsCopying(false);
      }, 2000);

      return () => clearTimeout(timer);
    } catch (error) {
      console.error('Failed to copy summary:', error);
      setIsCopying(false);
    }
  };

  const { sendQuery } = useAIQuery();

  useEffect(() => {
    if (details && request) {
      generateSummary();
    }
  }, [request?.id]);

  const generateSummary = async () => {
    if (!details || !request) return;

    setLoading(true);
    setError(null);

    try {
      // Transform the request data into the expected format
      const requestData = {
        type: details.isFlaggedCell ? 'flagged cell' : details.isFlaggedTransaction ? 'transaction' : 'custom request',
        status: request.status.toLowerCase() as 'open' | 'in-progress' | 'closed',
        blocker: details.hasBlockerPriority,
        reportType: null, // We don't have this information readily available
        trxInfo: {
          type: details.transactionName,
          account: details.transactionAccount,
          amount:
            typeof details.transactionAmount === 'number'
              ? details.transactionAmount
              : typeof details.flaggedAmount === 'number'
                ? details.flaggedAmount
                : 0,
          trxDate: details.trxDate,
          description: details.transactionMemo,
        },
        messages:
          request.comments?.map(comment => ({
            user: comment.user?.name || comment.userLocation?.email?.split('@')[0] || 'Unknown',
            date: new Date(comment.createdAt).toISOString(),
            message: comment.comment,
            role: comment.userLocation?.role || 'USER',
          })) || [],

        extendedContext,
      };

      // Send the query to the AI
      const prompt = summarizePrompt(JSON.stringify(requestData, null, 2));
      const response = await sendQuery(prompt, AIQueryTypes.Summarize);

      if (response.content) {
        // Create a properly typed response object for the parser
        const aiResponse = {
          content: response.content, // We've already checked this exists
          model: response.model,
          provider: response.provider,
          timestamp: new Date().toISOString(),
          ...response, // Include any other properties
        };

        // Use the AI JSON Parser to handle the response
        const parseResult = parseAIResponse(aiResponse, { debug: true });

        if ('error' in parseResult) {
          // Handle parsing error
          const errorMsg = `Failed to parse AI response: ${parseResult.message}`;
          setError(errorMsg);
          logger.msg('AI Response Parsing Error:', parseResult);
        } else {
          // Successfully parsed the response
          //logger.msg('Parsed AI Response:', parseResult);
          setSummary(parseResult as SummaryResponse);
        }
      } else {
        setError('No response from AI');
        logger.msg('No response from AI', response.content);
      }
    } catch (err) {
      console.error('Error generating summary:', err);
      setError('Failed to generate summary');
    } finally {
      setLoading(false);
    }
  };

  if (!details) return <div>Loading request details...</div>;

  // Configure DOMPurify for HTML sanitization
  const purifyConfig = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'a', 'ul', 'ol', 'li', 'p', 'br', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class'],
    ADD_ATTR: ['target', 'rel'],
    KEEP_CONTENT: true,
  };

  return (
    <div className={`summarize-panel ${className || ''}`}>
      <div className="summarize-panel-section">
        {loading ? (
          <KAILoader message="Generating summary" small={true} />
        ) : error ? (
          <div className="summarize-panel-error">
            <p>{error}</p>
            <button onClick={generateSummary} className="kai-retry-button">
              Retry
            </button>
          </div>
        ) : summary ? (
          <>
            <div className="summarize-panel-item">
              <h4>Summary</h4>
              <div
                className="summarize-panel-summary"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(summary.formattedSummary || summary.summary, purifyConfig),
                }}
              />
              {/* Feedback */}
              <div className="summarize-panel-feedback">
                {/* <Whisper trigger="hover" delayOpen={400} delayClose={10} placement="top" speaker={<Tooltip>Like</Tooltip>}>    
                <div className="summarize-panel-feedback-button">
                  <IconThumbsUp />
                </div>
                </Whisper>
                <Whisper trigger="hover" delayOpen={400} delayClose={10} placement="top" speaker={<Tooltip>Dislike</Tooltip>}>    
                <div className="summarize-panel-feedback-button">
                  <IconThumbsDown />
                </div>
                </Whisper> */}
                <Whisper
                  trigger="hover"
                  delayOpen={400}
                  delayClose={10}
                  placement="left"
                  speaker={<Tooltip>Copy to clipboard</Tooltip>}
                >
                  <div
                    className={`summarize-panel-feedback-button ${isCopying ? 'is-copying' : ''}`}
                    onClick={handleCopySummary}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && handleCopySummary()}
                  >
                    {isCopying ? <IconCheck /> : <IconCopyNew />}
                  </div>
                </Whisper>
              </div>
            </div>

            {isAccountant && (
              <>
                {summary.actions.length > 0 && (
                  <div className="summarize-panel-item">
                    <h4>Recommended Actions</h4>
                    <ul className="summarize-panel-actions">
                      {summary.actions.map((action, index) => (
                        <li
                          key={index}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(action, purifyConfig),
                          }}
                        />
                      ))}
                    </ul>
                  </div>
                )}

                <div className="summarize-panel-item">
                  <h4>Priority</h4>
                  <div className="summarize-panel-priority">
                    <span className={`priority-badge ${summary.priority.level}`}>
                      {summary.priority.level.toUpperCase()}
                    </span>
                    <span className="priority-reason">{summary.priority.reason}</span>
                  </div>
                  <div className="summarize-panel-confidence">
                    Confidence: {Math.round(summary.confidenceScore * 100)}%
                  </div>
                </div>
              </>
            )}
            <div className="summarize-panel-warning">
              <b>Kitchensync AI</b> can make mistakes. Please double-check responses.
            </div>
          </>
        ) : (
          <div className="summarize-panel-item">
            <button onClick={generateSummary} className="kai-generate-button">
              Generate AI Summary
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SummarizePanel;
